import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { tableSchema, type TableFormData } from '@/lib/schemas/table.schema';
import { logError } from '@/lib/logger';
import { BlockStack, Modal, Select, TextField } from '@/lib/polaris';
import { toast } from 'sonner';

interface Table {
  id?: string;
  table_number: string;
  name?: string | null;
  capacity: number;
  status: 'free' | 'occupied' | 'reserved' | 'maintenance';
  location?: string | null;
  notes?: string | null;
  branch_id: string;
}

interface TableFormModalProps {
  open: boolean;
  onClose: () => void;
  table: Table | null;
  onSuccess: () => void;
}

const _DEFAULT_TABLE: Omit<Table, 'branch_id'> = {
  table_number: '',
  name: null,
  capacity: 4,
  status: 'free',
  location: null,
  notes: null,
};

export function TableFormModal({
  open,
  onClose,
  table,
  onSuccess,
}: TableFormModalProps) {
  const { branchId } = useAuth();
  const [loading, setLoading] = useState(false);

  // React Hook Form con validación Zod
  const form = useForm<TableFormData>({
    resolver: zodResolver(tableSchema),
    defaultValues: {
      table_number: '',
      name: null,
      capacity: 4,
      status: 'free',
      location: null,
      notes: null,
    },
  });

  // Actualizar formulario cuando cambia la mesa
  useEffect(() => {
    if (open && table) {
      form.reset({
        table_number: table.table_number,
        name: table.name || null,
        capacity: table.capacity,
        status: table.status,
        location: table.location || null,
        notes: table.notes || null,
      });
    } else if (open && !table) {
      form.reset({
        table_number: '',
        name: null,
        capacity: 4,
        status: 'free',
        location: null,
        notes: null,
      });
    }
  }, [open, table, form]);

  const handleSubmit = async (data: TableFormData) => {
    setLoading(true);

    try {
      // Validar branchId
      if (!branchId) {
        throw new Error('No se encontró la sucursal. Por favor selecciona una sucursal.');
      }

      if (table?.id) {
        // ACTUALIZAR mesa existente
        const { error } = await supabase
          .from('tables')
          .update({
            table_number: data.table_number,
            name: data.name || null,
            capacity: data.capacity,
            status: data.status,
            location: data.location || null,
            notes: data.notes || null,
          })
          .eq('id', table.id);

        if (error) throw error;

        toast.success('Mesa actualizada', {
          description: `Mesa ${data.table_number} ha sido actualizada correctamente`,
        });
      } else {
        // CREAR nueva mesa
        const { error } = await supabase
          .from('tables')
          .insert({
            branch_id: branchId,
            table_number: data.table_number,
            name: data.name || null,
            capacity: data.capacity,
            status: data.status,
            location: data.location || null,
            notes: data.notes || null,
          });

        if (error) throw error;

        toast.success('Mesa creada', {
          description: `Mesa ${data.table_number} ha sido agregada`,
        });
      }

      form.reset();
      onSuccess();
      onClose();
    } catch (error: unknown) {
      logError('Error al guardar mesa', error);
      toast.error('Error al guardar', {
        description: (error as Error)?.message || 'Intenta nuevamente',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const values = form.watch();
  const errors = form.formState.errors;

  const statusOptions = [
    { label: 'Libre', value: 'free' },
    { label: 'Ocupada', value: 'occupied' },
    { label: 'Reservada', value: 'reserved' },
    { label: 'Mantenimiento', value: 'maintenance' },
  ];

  return (
    <Modal
      open={open}
      onClose={() => {
        if (loading) return;
        handleClose();
      }}
      title={table ? 'Editar mesa' : 'Nueva mesa'}
      primaryAction={{
        content: loading ? 'Guardando…' : 'Guardar',
        loading,
        onAction: () => form.handleSubmit(handleSubmit)(),
      }}
      secondaryActions={[
        {
          content: 'Cancelar',
          onAction: handleClose,
          disabled: loading,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <TextField
            label="Número de mesa"
            value={values.table_number || ''}
            onChange={(v) => form.setValue('table_number', v, { shouldValidate: true })}
            autoComplete="off"
            placeholder="Ej: 1, 2, 3, A1, B2…"
            error={errors.table_number?.message}
          />

          <TextField
            label="Nombre (opcional)"
            value={values.name ?? ''}
            onChange={(v) => form.setValue('name', v ? v : null, { shouldValidate: true })}
            autoComplete="off"
            placeholder="Ej: Mesa VIP, Terraza 1…"
            error={errors.name?.message}
          />

          <TextField
            label="Capacidad"
            type="number"
            value={String(values.capacity ?? 4)}
            onChange={(v) => {
              const parsed = Number.parseInt(v || '0', 10);
              form.setValue('capacity', Number.isFinite(parsed) && parsed > 0 ? parsed : 4, { shouldValidate: true });
            }}
            autoComplete="off"
            error={errors.capacity?.message}
          />

          <Select
            label="Estado"
            options={statusOptions}
            value={values.status || 'free'}
            onChange={(v) => form.setValue('status', v as TableFormData['status'], { shouldValidate: true })}
          />

          <TextField
            label="Ubicación (opcional)"
            value={values.location ?? ''}
            onChange={(v) => form.setValue('location', v ? v : null, { shouldValidate: true })}
            autoComplete="off"
            placeholder="Ej: Interior, Exterior, Terraza…"
            error={errors.location?.message}
          />

          <TextField
            label="Notas (opcional)"
            value={values.notes ?? ''}
            onChange={(v) => form.setValue('notes', v ? v : null, { shouldValidate: true })}
            autoComplete="off"
            multiline={4}
            placeholder="Notas adicionales sobre la mesa…"
            error={errors.notes?.message}
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
